"use client"

import { CheckCircle2Icon, CircleAlertIcon } from "lucide-react"

import { Card, CardContent, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { formatMoney } from "@/features/finance/lib/finance-utils"
import type { ReconciliationOpeningCheck } from "@/features/finance/model/finance-close-trust-types"
import { MobileFormSection } from "@/shared/components/mobile-form"
import { useIsMobile } from "@/shared/hooks/use-mobile"

/**
 * Which period a disagreement actually started in.
 *
 * A closing-only reconciliation can say "the bank and the books differ" but not *when* they started to. This
 * splits the question in two: if the OPENING balances already disagree, the fault predates this period and
 * re-examining this month's transactions is wasted effort. If the openings match but the movements do not,
 * the error is genuinely inside this period and the line list is the right place to look.
 *
 * That verdict is the point of the panel — the six figures are supporting evidence for it.
 */
export function ReconciliationOpeningCheckPanel({
  check,
  currency,
}: {
  check: ReconciliationOpeningCheck
  currency: string
}) {
  const t = useTranslations("finance.reconciliation.openingCheck")
  const isMobile = useIsMobile()

  // Openings first: when they disagree the movement comparison is not the thing to act on, whatever it says.
  const verdictKey = !check.openingMatches
    ? "verdict.earlierPeriod"
    : !check.movementMatches
      ? "verdict.thisPeriod"
      : "verdict.balanced"
  const settled = check.openingMatches && check.movementMatches

  if (isMobile) {
    // Phone: the verdict leads the group, then one row per comparison with its difference on the right and the
    // two figures behind it underneath.
    const row = (label: string, statement: string, system: string, difference: string, matches: boolean) => (
      <div className='flex flex-col gap-0.5 px-4 py-2.5'>
        <span className='flex min-w-0 items-center justify-between gap-3'>
          <span className='truncate text-base font-semibold'>{label}</span>
          <span
            className={cn(
              "shrink-0 text-base font-extrabold tabular-nums",
              matches ? "text-primary" : "text-amber-600 dark:text-amber-400",
            )}
          >
            {formatMoney(difference, currency)}
          </span>
        </span>
        <span className='truncate text-xs font-semibold text-muted-foreground tabular-nums'>
          {t("statement")} {formatMoney(statement, currency)} · {t("ibada")} {formatMoney(system, currency)}
        </span>
      </div>
    )
    return (
      <MobileFormSection>
        <div className='flex items-start gap-2.5 px-4 py-3'>
          {settled ? (
            <CheckCircle2Icon aria-hidden className='mt-0.5 size-5 shrink-0 text-primary' />
          ) : (
            <CircleAlertIcon aria-hidden className='mt-0.5 size-5 shrink-0 text-amber-500' />
          )}
          <p className='text-[15px] font-semibold'>{t(verdictKey as never)}</p>
        </div>
        {row(t("opening"), check.statementOpeningBalance, check.systemOpeningBalance, check.openingDifference, check.openingMatches)}
        {row(t("movement"), check.statementMovement, check.systemMovement, check.movementDifference, check.movementMatches)}
      </MobileFormSection>
    )
  }

  return (
    <Card size='sm'>
      <CardContent className='space-y-3'>
        <div className='flex items-start gap-2'>
          {settled ? (
            <CheckCircle2Icon className='mt-0.5 size-4 shrink-0 text-emerald-500' />
          ) : (
            <CircleAlertIcon className='mt-0.5 size-4 shrink-0 text-amber-500' />
          )}
          <p className='text-sm font-medium'>{t(verdictKey as never)}</p>
        </div>

        <dl className='grid gap-3 sm:grid-cols-2'>
          <Comparison
            label={t("opening")}
            statement={check.statementOpeningBalance}
            system={check.systemOpeningBalance}
            difference={check.openingDifference}
            matches={check.openingMatches}
            currency={currency}
            statementLabel={t("statement")}
            systemLabel={t("ibada")}
          />
          <Comparison
            label={t("movement")}
            statement={check.statementMovement}
            system={check.systemMovement}
            difference={check.movementDifference}
            matches={check.movementMatches}
            currency={currency}
            statementLabel={t("statement")}
            systemLabel={t("ibada")}
          />
        </dl>
      </CardContent>
    </Card>
  )
}

function Comparison({
  label,
  statement,
  system,
  difference,
  matches,
  currency,
  statementLabel,
  systemLabel,
}: {
  label: string
  statement: string
  system: string
  difference: string
  matches: boolean
  currency: string
  statementLabel: string
  systemLabel: string
}) {
  return (
    <div className='rounded-md border p-2.5'>
      <dt className='flex items-center justify-between gap-2'>
        <span className='text-xs font-medium'>{label}</span>
        <span
          className={cn(
            "font-mono text-xs tabular-nums",
            matches ? "text-emerald-500" : "text-amber-500",
          )}
        >
          {formatMoney(difference, currency)}
        </span>
      </dt>
      <dd className='text-muted-foreground mt-1 flex items-center justify-between gap-2 text-xs'>
        <span>
          {statementLabel} <span className='font-mono tabular-nums'>{formatMoney(statement, currency)}</span>
        </span>
        <span>
          {systemLabel} <span className='font-mono tabular-nums'>{formatMoney(system, currency)}</span>
        </span>
      </dd>
    </div>
  )
}
